import { Flex, Paragraph, Text } from '@buffer-mono/popcorn'

import { BillingIntervalValues, OBPlanIdValues } from '../../types'
import type { PlanInfo } from '../../types'
import { usePlanSelector } from '../../hooks/usePlanSelector'
import { formatPrice } from '../../utils/pricingCalculations'

import styles from './CurrentPlanInfo.module.css'

import type { JSX } from 'react'

interface CurrentPlanInfoProps {
  currentPlan: PlanInfo
  isCanceledAtPeriodEnd?: boolean
  periodEnd?: string | null
}

/**
 * Displays information about the user's current billing plan, including plan name,
 * channel count, and pricing details. Formats pricing based on billing cycle and
 * provides guidance for plan customization.
 *
 * @param currentPlan - The user's current plan information including name, pricing, and channels
 */
const CurrentPlanInfo = ({
  currentPlan,
  isCanceledAtPeriodEnd = false,
  periodEnd,
}: CurrentPlanInfoProps): JSX.Element => {
  const { currentUser } = usePlanSelector()
  const trial = currentUser?.trial
  const billingPeriod =
    currentPlan.billingCycle === BillingIntervalValues.year ? 'yr' : 'mo'
  const isFree = currentPlan.billingCyclePrice === 0
  const isEssentials = currentPlan.planId === OBPlanIdValues.essentials
  const isTeam = currentPlan.planId === OBPlanIdValues.team
  const isPaidPlan = isEssentials || isTeam

  // Format effective date for display
  const formatEffectiveDate = (dateString: string): string => {
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return dateString
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    })
  }

  // Calculate trial days remaining
  const getTrialInfo = (): {
    daysRemaining: number
    formattedEndDate: string
  } | null => {
    if (!trial?.endDate) return null
    const endDate = new Date(trial.endDate)
    const today = new Date()
    const daysRemaining = Math.ceil(
      // (1000 * 60 * 60 * 24): milliseconds to days
      (endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    )
    return {
      daysRemaining: Math.max(0, daysRemaining),
      formattedEndDate: formatEffectiveDate(trial.endDate),
    }
  }

  const trialInfo = getTrialInfo()
  const isOnTrial = trialInfo !== null

  const totalPrice = isPaidPlan && !isFree ? currentUser.currentlyPayingRaw : 0

  // Render trial layout
  const renderTrialLayout = (): JSX.Element => (
    <Text size="md">
      You&apos;re on a{' '}
      <Text as="span" weight="bold">
        free trial
      </Text>
      , ending in {trialInfo?.daysRemaining}{' '}
      {trialInfo?.daysRemaining === 1 ? 'day' : 'days'} on{' '}
      {trialInfo?.formattedEndDate}.
    </Text>
  )

  // Render free plan layout
  const renderFreeLayout = (): JSX.Element => (
    <Text size="md">
      You&apos;re on the{' '}
      <Text as="span" weight="bold">
        Free
      </Text>{' '}
      plan with up to 3 channels.
    </Text>
  )

  // Render paid plan layout (Essentials/Team)
  const renderPaidLayout = (): JSX.Element => (
    <Text size="md">
      You&apos;re on the{' '}
      <Text as="span" weight="bold">
        {currentPlan.planName}
      </Text>{' '}
      plan at {formatPrice(totalPrice)}/{billingPeriod} for{' '}
      {currentPlan.channelCount}{' '}
      {currentPlan.channelCount === 1 ? 'channel' : 'channels'}.
    </Text>
  )

  // Render canceled plan layout
  const renderCanceledLayout = (): JSX.Element => (
    <Text size="md">
      You can continue using the{' '}
      <Text as="span" weight="bold">
        {currentPlan.planName}
      </Text>{' '}
      features until the end of your billing cycle on{' '}
      <Text as="span" weight="bold">
        {periodEnd ? formatEffectiveDate(periodEnd) : 'N/A'}
      </Text>
      , when your Free subscription starts.
    </Text>
  )

  return (
    <Flex
      role="region"
      aria-label="Current plan information"
      fullWidth
      className={styles.currentPlanInfo}
    >
      <Paragraph size="md">
        {isOnTrial ? (
          renderTrialLayout()
        ) : isCanceledAtPeriodEnd && isPaidPlan ? (
          renderCanceledLayout()
        ) : isFree ? (
          renderFreeLayout()
        ) : isPaidPlan ? (
          renderPaidLayout()
        ) : (
          // Fallback to original layout for any other plan types
          <Text size="md">
            You're on the{' '}
            <Text as="span" weight="bold" color="default">
              {currentPlan.planName}
            </Text>{' '}
            plan with{' '}
            <Text as="span" weight="bold" color="default">
              {currentPlan.channelCount}{' '}
              {currentPlan.channelCount === 1 ? 'channel' : 'channels'}
            </Text>
            , paying{' '}
            <Text as="span" weight="bold" color="default">
              {formatPrice(currentPlan.billingCyclePrice)}/{billingPeriod}
            </Text>
            .
          </Text>
        )}
        <br />
        Add or remove channels to customize your plan.
      </Paragraph>
    </Flex>
  )
}

export default CurrentPlanInfo
